<script setup lang="ts">
  import { Typo } from '@travelata-libs/ui-kit';
  import { computed } from 'vue';

  interface ProgressProps {
    value?: number;
    title?: string;
  }

  const props = withDefaults(defineProps<ProgressProps>(), {
    value: 0,
    title: '',
  });

  const MAX_STEPS = 5;

  const progressFill = computed(() => {
    if (props.value >= 4) return 'high';

    if (props.value >= 3 && props.value < 4) return 'medium';

    return 'low';
  });

  const filledCount = computed(() => {
    if (!props.value) return 0;

    const progressValue = Math.floor(props.value || 0);
    return Math.min(Math.max(progressValue, 1), MAX_STEPS);
  });

  const progressText = computed(() => {
    return new Intl.NumberFormat('ru-Ru', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(props.value);
  });
</script>

<template>
  <div class="tl-progress">
    <Typo class="tl-progress__value" variant="body-m-500" data-test-id="progress-text">{{ progressText }}</Typo>
    <div class="tl-progress__steps">
      <div
        v-for="step in MAX_STEPS"
        :key="step"
        :class="[
          {
            [`tl-progress__step_fill-${progressFill}`]: filledCount >= step,
          },
          'tl-progress__step',
        ]"
        data-test-id="step"
      ></div>
    </div>
    <Typo variant="body-l-400">{{ title }}</Typo>
  </div>
</template>

<style lang="scss" scoped>
  .tl-progress {
    align-items: center;
    display: flex;
    gap: var(--spacing-xs);
    height: 20px;

    &__value {
      align-items: center;
      background-color: var(--color-neutral-10);
      border-radius: var(--rounding-xs);
      box-sizing: border-box;
      display: flex;
      justify-content: center;
      padding: var(--spacing-xxxs) var(--spacing-xxs);
      width: 34px;
    }

    &__steps {
      display: flex;
      gap: var(--spacing-xxxs);
    }

    &__step {
      background-color: var(--color-neutral-10);
      border-radius: var(--rounding-s);
      height: 4px;
      width: 14px;

      &_fill {
        &-low {
          background-color: var(--color-orange-30);
        }

        &-medium {
          background-color: var(--color-lime-50);
        }

        &-high {
          background-color: var(--color-green-50);
        }
      }
    }
  }
</style>
